/**
 * The terrain of the district (pure, no three.js; unit tested): gentle rolling ground of about
 * +-3 m wherever nothing is built, exactly flat (y = 0) under parcels, streets, sidewalks and
 * building footprints, with a smooth blend between the two. The same numbers drive the drawn
 * terrain (`look/Terrain.tsx`, which samples the mask texture built here), the walker's floor, the
 * vehicle heightfield collider and everything that stands on the ground, so they always agree.
 *
 * The data is a grid of RGBA bytes over the district plus a margin:
 *  - R: how much of the rolling height applies (0 flat .. 255 free ground);
 *  - G: vegetation (grass blades, flowers) allowed (0 on pavement, 255 on grass and lawns);
 *  - B: road shoulder (gravel next to the asphalt), 255 at the road edge fading out over a few m;
 *  - A: the top of the surface above the terrain (pads and lawns, in units of 1/255 * LIFT_RANGE).
 * Outside the grid the ground is free (R = 255, G = 255).
 */

export const TERRAIN_AMP = 3;
/** Meters of smooth transition between a flat zone and the free rolling ground. */
export const TERRAIN_BLEND = 22;
/** Flat zones grow by this much before the blend starts (the ground around a parcel stays level). */
export const TERRAIN_MARGIN = 3;
/** Meters beyond the flat zones that the grid covers. */
export const TERRAIN_PAD = 280;
/** Alpha channel scale: A / 255 * LIFT_RANGE meters. */
export const LIFT_RANGE = 0.6;
/** The grid never exceeds this many cells per side (the cell grows instead). */
export const MAX_CELLS = 2048;

export interface FlatRect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface TerrainInput {
  /** Everything that must be level (parcels, roads, footprints). */
  flat: readonly FlatRect[];
  /** Asphalt (for the gravel shoulder). */
  roads: readonly FlatRect[];
  /** Pavement (no grass): sidewalks, pads, paths, footprints. */
  paved: readonly FlatRect[];
  /** Grass on top of pavement (lawns), with their surface height. */
  lawns: readonly (FlatRect & { top: number })[];
  /** Raised surfaces (pads, sidewalks) with their top height above the terrain. */
  lifts: readonly (FlatRect & { top: number })[];
}

export interface TerrainData {
  originX: number;
  originZ: number;
  /** Cell size (meters) and cell counts; samples sit at the cell corners. */
  cell: number;
  nx: number;
  nz: number;
  /** nx * nz * 4 bytes, row-major over z (index = (iz * nx + ix) * 4). */
  data: Uint8Array;
  /** Centre of the district (the amplitude tapers with the distance from it). */
  cx: number;
  cz: number;
  /** Bumped by every rebuild (consumers memoise on it). */
  version: number;
}

/**
 * Rolling height in -1..1 from a handful of rotated waves (deterministic, the same expression in
 * GLSL below), tapered to 40 % far from the district so the horizon stays calm.
 */
const WAVES: readonly [number, number, number, number, number][] = [
  // direction x, direction z, wavelength (m), amplitude, phase
  [0.8, 0.6, 210, 1.0, 0.3],
  [-0.45, 0.89, 133, 0.7, 1.7],
  [0.96, -0.28, 87, 0.42, 4.1],
  [-0.7, -0.71, 51, 0.24, 2.6],
  [0.17, 0.98, 33, 0.12, 5.3],
];
const WAVE_SUM = WAVES.reduce((n, w) => n + w[3], 0);

export function rolling(x: number, z: number): number {
  let h = 0;
  for (const [dx, dz, len, amp, ph] of WAVES) {
    const a = ((dx * x + dz * z) / len) * Math.PI * 2 + ph;
    // A product with a second, crossing wave breaks the parallel ridges into knolls.
    const b = ((-dz * x + dx * z) / (len * 1.7)) * Math.PI * 2 + ph * 0.5;
    h += amp * Math.sin(a) * (0.65 + 0.35 * Math.cos(b));
  }
  return h / WAVE_SUM;
}

export function taper(t: Pick<TerrainData, "cx" | "cz"> | null, x: number, z: number): number {
  if (!t) return 1;
  const r = Math.hypot(x - t.cx, z - t.cz);
  const k = Math.min(1, Math.max(0, (r - 700) / 900));
  return 1 - 0.6 * k * k * (3 - 2 * k);
}

const smooth = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** Two-pass chamfer distance (in cells, 1 / sqrt 2 weights) from the cells marked 0. */
function distanceField(seed: Uint8Array, nx: number, nz: number): Float32Array {
  const INF = 1e9;
  const d = new Float32Array(nx * nz);
  for (let i = 0; i < d.length; i++) d[i] = seed[i] ? INF : 0;
  const D = Math.SQRT2;
  for (let z = 0; z < nz; z++) {
    for (let x = 0; x < nx; x++) {
      const i = z * nx + x;
      let v = d[i]!;
      if (v === 0) continue;
      if (x > 0) v = Math.min(v, d[i - 1]! + 1);
      if (z > 0) {
        v = Math.min(v, d[i - nx]! + 1);
        if (x > 0) v = Math.min(v, d[i - nx - 1]! + D);
        if (x < nx - 1) v = Math.min(v, d[i - nx + 1]! + D);
      }
      d[i] = v;
    }
  }
  for (let z = nz - 1; z >= 0; z--) {
    for (let x = nx - 1; x >= 0; x--) {
      const i = z * nx + x;
      let v = d[i]!;
      if (v === 0) continue;
      if (x < nx - 1) v = Math.min(v, d[i + 1]! + 1);
      if (z < nz - 1) {
        v = Math.min(v, d[i + nx]! + 1);
        if (x < nx - 1) v = Math.min(v, d[i + nx + 1]! + D);
        if (x > 0) v = Math.min(v, d[i + nx - 1]! + D);
      }
      d[i] = v;
    }
  }
  return d;
}

let builds = 0;

/** Builds the terrain grid (see the module comment). An empty input gives a small free grid. */
export function buildTerrain(input: TerrainInput): TerrainData {
  const all = [...input.flat, ...input.paved];
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const r of all) {
    minX = Math.min(minX, r.minX);
    maxX = Math.max(maxX, r.maxX);
    minZ = Math.min(minZ, r.minZ);
    maxZ = Math.max(maxZ, r.maxZ);
  }
  if (!Number.isFinite(minX)) {
    minX = -100;
    maxX = 100;
    minZ = -100;
    maxZ = 100;
  }
  minX -= TERRAIN_PAD;
  maxX += TERRAIN_PAD;
  minZ -= TERRAIN_PAD;
  maxZ += TERRAIN_PAD;
  const span = Math.max(maxX - minX, maxZ - minZ);
  // 2 m cells at least: the blend and the pavement edges need no more, and the build stays quick.
  const cell = Math.max(2, Math.ceil(span / (MAX_CELLS - 1)));
  const nx = Math.ceil((maxX - minX) / cell) + 1;
  const nz = Math.ceil((maxZ - minZ) / cell) + 1;
  const originX = Math.floor(minX / cell) * cell;
  const originZ = Math.floor(minZ / cell) * cell;
  const n = nx * nz;
  const data = new Uint8Array(n * 4);

  // Rasterises rects (grown by `grow` meters) into a byte mask: `inside` is written in the rect.
  const raster = (
    rects: readonly FlatRect[],
    grow: number,
    fn: (i: number, r: FlatRect) => void,
  ): void => {
    for (const r of rects) {
      const x0 = Math.max(0, Math.ceil((r.minX - grow - originX) / cell));
      const x1 = Math.min(nx - 1, Math.floor((r.maxX + grow - originX) / cell));
      const z0 = Math.max(0, Math.ceil((r.minZ - grow - originZ) / cell));
      const z1 = Math.min(nz - 1, Math.floor((r.maxZ + grow - originZ) / cell));
      for (let z = z0; z <= z1; z++) for (let x = x0; x <= x1; x++) fn(z * nx + x, r);
    }
  };

  // R: distance from the flat zones (grown by the margin), smoothed over the blend.
  const seed = new Uint8Array(n).fill(1);
  raster([...input.flat, ...input.paved], TERRAIN_MARGIN, (i) => (seed[i] = 0));
  const dist = distanceField(seed, nx, nz);
  // B: distance from the asphalt.
  const roadSeed = new Uint8Array(n).fill(1);
  raster(input.roads, 0, (i) => (roadSeed[i] = 0));
  const roadDist = distanceField(roadSeed, nx, nz);
  for (let i = 0; i < n; i++) {
    data[i * 4] = Math.round(smooth(0, TERRAIN_BLEND, dist[i]! * cell) * 255);
    data[i * 4 + 1] = 255;
    const rd = roadDist[i]! * cell;
    data[i * 4 + 2] = rd > 0 ? Math.round((1 - smooth(0.2, 3.5, rd)) * 255) : 0;
  }
  // G and A: pavement has no grass; lawns bring it back; raised tops.
  raster(input.paved, 0, (i) => (data[i * 4 + 1] = 0));
  raster(input.roads, 0, (i) => (data[i * 4 + 1] = 0));
  raster(input.lifts, 0, (i, r) => {
    const top = (r as FlatRect & { top: number }).top;
    data[i * 4 + 3] = Math.max(data[i * 4 + 3]!, Math.round((top / LIFT_RANGE) * 255));
  });
  raster(input.lawns, -0.5, (i, r) => {
    data[i * 4 + 1] = 255;
    data[i * 4 + 3] = Math.round(((r as FlatRect & { top: number }).top / LIFT_RANGE) * 255);
  });
  return {
    originX,
    originZ,
    cell,
    nx,
    nz,
    data,
    cx: (minX + maxX) / 2,
    cz: (minZ + maxZ) / 2,
    version: ++builds,
  };
}

/** Bilinear sample of channel `c` (0..1) at a world point; free ground outside the grid. */
export function sampleChannel(
  t: TerrainData | null,
  c: 0 | 1 | 2 | 3,
  x: number,
  z: number,
): number {
  if (!t) return c === 0 || c === 1 ? 1 : 0;
  const fx = (x - t.originX) / t.cell;
  const fz = (z - t.originZ) / t.cell;
  if (fx < 0 || fz < 0 || fx > t.nx - 1 || fz > t.nz - 1) return c === 0 || c === 1 ? 1 : 0;
  const x0 = Math.min(t.nx - 2, Math.floor(fx));
  const z0 = Math.min(t.nz - 2, Math.floor(fz));
  const ax = fx - x0;
  const az = fz - z0;
  const at = (ix: number, iz: number) => t.data[(iz * t.nx + ix) * 4 + c]! / 255;
  const a = at(x0, z0) + (at(x0 + 1, z0) - at(x0, z0)) * ax;
  const b = at(x0, z0 + 1) + (at(x0 + 1, z0 + 1) - at(x0, z0 + 1)) * ax;
  return a + (b - a) * az;
}

/** Terrain height at a world point (meters). */
export function terrainHeight(t: TerrainData | null, x: number, z: number): number {
  const m = sampleChannel(t, 0, x, z);
  if (m <= 0) return 0;
  return rolling(x, z) * TERRAIN_AMP * m * taper(t, x, z);
}

/** Unit normal of the terrain at a point (finite differences over 1 m). */
export function terrainNormal(
  t: TerrainData | null,
  x: number,
  z: number,
): [number, number, number] {
  const e = 1;
  const hx = terrainHeight(t, x + e, z) - terrainHeight(t, x - e, z);
  const hz = terrainHeight(t, x, z + e) - terrainHeight(t, x, z - e);
  const nx = -hx / (2 * e);
  const nz = -hz / (2 * e);
  const len = Math.hypot(nx, 1, nz);
  return [nx / len, 1 / len, nz / len];
}

/**
 * Heights for a Rapier heightfield over the grid (subsampled to at most `maxSide` samples a side):
 * column-major (rows along Z, columns along X), as `ColliderDesc.heightfield` takes them.
 */
export function heightfieldOf(
  t: TerrainData,
  maxSide = 512,
): {
  rows: number;
  cols: number;
  heights: Float32Array;
  sizeX: number;
  sizeZ: number;
  cx: number;
  cz: number;
} {
  const spanX = (t.nx - 1) * t.cell;
  const spanZ = (t.nz - 1) * t.cell;
  const step = Math.max(t.cell, Math.ceil(Math.max(spanX, spanZ) / (maxSide - 1)));
  const cols = Math.max(1, Math.floor(spanX / step));
  const rows = Math.max(1, Math.floor(spanZ / step));
  const heights = new Float32Array((rows + 1) * (cols + 1));
  for (let j = 0; j <= cols; j++) {
    for (let i = 0; i <= rows; i++) {
      heights[j * (rows + 1) + i] = terrainHeight(t, t.originX + j * step, t.originZ + i * step);
    }
  }
  return {
    rows,
    cols,
    heights,
    sizeX: cols * step,
    sizeZ: rows * step,
    cx: t.originX + (cols * step) / 2,
    cz: t.originZ + (rows * step) / 2,
  };
}

/** GLSL twin of `rolling`, `taper` and `terrainHeight` (sampler `uTerr`, RGBA8, nearest). */
export const TERRAIN_GLSL = /* glsl */ `
uniform highp sampler2D uTerr;
uniform vec4 uTerrGrid; // originX, originZ, cell, 0
uniform vec4 uTerrInfo; // nx, nz, cx, cz
float terrRolling(vec2 p) {
  float h = 0.0;
  ${WAVES.map(
    ([dx, dz, len, amp, ph]) =>
      `h += ${amp.toFixed(4)} * sin((${dx.toFixed(4)} * p.x + ${dz.toFixed(4)} * p.y) * ${((Math.PI * 2) / len).toFixed(7)} + ${ph.toFixed(4)}) * (0.65 + 0.35 * cos((${(-dz).toFixed(4)} * p.x + ${dx.toFixed(4)} * p.y) * ${((Math.PI * 2) / (len * 1.7)).toFixed(7)} + ${(ph * 0.5).toFixed(4)}));`,
  ).join("\n  ")}
  return h / ${WAVE_SUM.toFixed(4)};
}
float terrTaper(vec2 p) {
  float k = clamp((length(p - uTerrInfo.zw) - 700.0) / 900.0, 0.0, 1.0);
  return 1.0 - 0.6 * k * k * (3.0 - 2.0 * k);
}
vec4 terrSample(vec2 p) {
  vec2 f = (p - uTerrGrid.xy) / uTerrGrid.z;
  vec2 n = uTerrInfo.xy;
  if (f.x < 0.0 || f.y < 0.0 || f.x > n.x - 1.0 || f.y > n.y - 1.0) return vec4(1.0, 1.0, 0.0, 0.0);
  vec2 i0 = min(floor(f), n - 2.0);
  vec2 a = f - i0;
  ivec2 c = ivec2(i0);
  vec4 s00 = texelFetch(uTerr, c, 0);
  vec4 s10 = texelFetch(uTerr, c + ivec2(1, 0), 0);
  vec4 s01 = texelFetch(uTerr, c + ivec2(0, 1), 0);
  vec4 s11 = texelFetch(uTerr, c + ivec2(1, 1), 0);
  return mix(mix(s00, s10, a.x), mix(s01, s11, a.x), a.y);
}
float terrHeight(vec2 p) {
  float m = terrSample(p).r;
  return terrRolling(p) * ${TERRAIN_AMP.toFixed(1)} * m * terrTaper(p);
}
`;
